import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Users, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/drivers").then((r) => setDrivers(r.data));
  useEffect(() => { load(); }, []);

  const createDriver = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/drivers", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      toast.success(`Chauffeur ${form.name} créé`);
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de la création");
    } finally {
      setBusy(false);
    }
  };

  const removeDriver = async (id, email) => {
    if (!window.confirm(`Supprimer le chauffeur ${email} ?`)) return;
    try {
      await api.delete(`/admin/drivers/${id}`);
      toast.success("Chauffeur supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec");
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="label-caps text-muted-foreground mb-2">Équipe</div>
            <h1 className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">Chauffeurs</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-driver-btn"
                className="h-12 px-6 rounded-none bg-primary text-primary-foreground font-bold btn-hard active:scale-95"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Nouveau chauffeur
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-2">
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl">Créer un compte chauffeur</DialogTitle>
              </DialogHeader>
              <form onSubmit={createDriver} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps">Nom complet</Label>
                  <Input
                    data-testid="new-driver-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-12 border-2 rounded-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Courriel</Label>
                  <Input
                    data-testid="new-driver-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="chauffeur@transprestige.ca"
                    className="h-12 border-2 rounded-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Mot de passe (6+ car.)</Label>
                  <Input
                    data-testid="new-driver-password"
                    type="password"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="h-12 border-2 rounded-none"
                  />
                  <p className="text-xs text-muted-foreground">Communiquez ce mot de passe temporaire au chauffeur. Il pourra le changer plus tard.</p>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-none border-2"
                    onClick={() => setOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    data-testid="new-driver-submit"
                    disabled={busy}
                    className="rounded-none bg-primary text-primary-foreground btn-hard"
                  >
                    {busy ? "Création…" : "Créer le compte"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {drivers.map((d) => (
            <Card key={d.id} className="rounded-none border-2 p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center font-display font-black text-lg shrink-0">
                {(d.name || d.email)[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-black text-lg truncate">{d.name || "—"}</div>
                <div className="text-sm text-muted-foreground truncate">{d.email}</div>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="rounded-none border-2 hover:bg-destructive hover:text-destructive-foreground shrink-0"
                onClick={() => removeDriver(d.id, d.email)}
                data-testid={`del-driver-${d.id}`}
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </Card>
          ))}
          {drivers.length === 0 && (
            <div className="col-span-2 text-center py-16 text-muted-foreground">
              <Users className="w-8 h-8 mx-auto mb-2" /> Aucun chauffeur inscrit.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
